import { useCallback, useState } from "react";
import { toast } from "sonner";
import { bulkProcessDistributions } from "@/Api/distributions";
import {
  BulkFailureType,
  BulkProcessResponseType,
  PendingActionType,
} from "../types";

const pluralize = (count: number, singular: string, plural: string) =>
  count === 1 ? singular : plural;

type UseBulkActionsParams = {
  /** Chamado após qualquer lote que tenha chegado ao backend (limpa seleção e refaz o fetch). */
  onFinished: () => Promise<void> | void;
};

/**
 * Confirmação e execução das ações em lote.
 *
 * Toda ação — individual, por concurso ou por seleção — passa por uma única
 * chamada a bulkProcessDistributions, então o caminho de código é sempre o mesmo.
 */
export const useBulkActions = ({ onFinished }: UseBulkActionsParams) => {
  const [pendingAction, setPendingAction] = useState<PendingActionType | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [failures, setFailures] = useState<BulkFailureType[] | null>(null);
  const [processedCount, setProcessedCount] = useState(0);

  const confirmAction = useCallback(async () => {
    if (!pendingAction) return;

    const { action, items } = pendingAction;

    if (items.length === 0) {
      setPendingAction(null);
      return;
    }

    setIsProcessing(true);

    try {
      const response = await bulkProcessDistributions({
        action,
        uuids: items.map((item) => item.uuid),
      });
      const payload = response?.data?.response as BulkProcessResponseType | undefined;

      // Erros de rede/HTTP já são notificados pelo interceptor do axios.
      if (!response?.ok || !payload) return;

      const labels = new Map(
        items.map((item) => [item.uuid, `${item.name} - ${item.classification}`])
      );
      const failed = payload.results.filter((result) => !result.success);
      const doneWord = pluralize(
        payload.processed,
        action === "accept" ? "aceita" : "rejeitada",
        action === "accept" ? "aceitas" : "rejeitadas"
      );

      if (payload.failed === 0) {
        toast.success(
          `${payload.processed} ${pluralize(payload.processed, "distribuição", "distribuições")} ${doneWord} com sucesso!`
        );
      } else if (payload.processed > 0) {
        toast.warning(
          `${payload.processed} processadas, ${payload.failed} falharam`
        );
        setProcessedCount(payload.processed);
        setFailures(
          failed.map((result) => ({
            uuid: result.uuid,
            label: labels.get(result.uuid) ?? result.uuid,
            error: result.error ?? "Erro desconhecido.",
          }))
        );
      } else {
        toast.error(
          failed[0]?.error || "Nenhuma distribuição pôde ser processada."
        );
      }

      await onFinished();
    } finally {
      setIsProcessing(false);
      setPendingAction(null);
    }
  }, [pendingAction, onFinished]);

  return {
    pendingAction,
    setPendingAction,
    isProcessing,
    failures,
    processedCount,
    closeFailures: () => setFailures(null),
    cancelAction: () => setPendingAction(null),
    confirmAction,
  };
};
